// src/pages/DeleteAccount.js

import React from "react";
import { Helmet } from "react-helmet";
import "../styles/Privacy.css";

const CONTACT_EMAIL = "privacy@ghrianghraffy.com";

const mailtoHref =
  `mailto:${CONTACT_EMAIL}` +
  "?subject=GhrianGhraffy%20account%20or%20data%20deletion%20request" +
  "&body=Please%20include%20the%20email%20address%20used%20for%20your%20GhrianGhraffy%20account%2C%20your%20school%20name%20if%20known%2C%20and%20whether%20you%20want%20your%20account%20deleted%20or%20only%20some%20data%20removed.";

const DeleteAccount = () => {
  return (
    <div className="privacy-page page">
      <Helmet>
        <title>Delete Account - GhrianGhraffy</title>
        <meta
          name="description"
          content="How to request deletion of your GhrianGhraffy account or associated data."
        />
      </Helmet>

      <div className="privacy-card">
        <header className="privacy-header">
          <p className="privacy-kicker">Account deletion</p>
          <h1 className="privacy-title">
            Delete your GhrianGhraffy account or data
          </h1>
          <p className="privacy-meta">Last updated: May 29, 2026</p>
        </header>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How to request deletion</h2>
          <p>
            To request deletion of your GhrianGhraffy account or associated
            personal data, email us at{" "}
            <a href={mailtoHref}>{CONTACT_EMAIL}</a> from the email address
            connected to your GhrianGhraffy account.
          </p>
          <p>
            Please include your account email address, your school name if you
            know it, and whether you want:
          </p>
          <ul>
            <li>your full GhrianGhraffy account deleted; or</li>
            <li>only some data removed, such as a profile photo or specific content.</li>
          </ul>
          <p>
            We may need to verify your identity before completing a deletion
            request. For school-managed content, we may also need to work with
            the relevant school before removing or retaining certain records.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">What we delete</h2>
          <p>
            When an account deletion request is accepted, we will delete or
            deactivate personal account data that is no longer needed to provide
            the service, such as:
          </p>
          <ul>
            <li>your GhrianGhraffy user account where deletion is appropriate;</li>
            <li>your profile details, such as display name and email address;</li>
            <li>your parent profile photo, where applicable;</li>
            <li>reset codes or account setup tokens linked to your account;</li>
            <li>
              photos or videos uploaded by your account, where deletion is
              appropriate and allowed by the school-controlled data model.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">What may be kept</h2>
          <p>
            Some data may need to be retained where required for legal,
            security, billing, audit, school safeguarding, or operational
            reasons. For example:
          </p>
          <ul>
            <li>
              school-controlled pupil photos may need to be reviewed with the
              school before deletion;
            </li>
            <li>
              billing or subscription records may need to be kept where required
              for accounting or legal obligations;
            </li>
            <li>
              security logs may be retained for a limited period to protect the
              service and investigate misuse;
            </li>
            <li>
              records may be kept where needed to comply with legal obligations
              or resolve disputes.
            </li>
          </ul>
          <p>
            If we cannot delete certain data immediately, we will explain why
            and, where possible, tell you how long it is expected to be retained.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Deleting some data only</h2>
          <p>
            You can also request deletion of some data without deleting your
            whole account. For example, you may ask us to remove a parent
            profile photo, correct account information, or review specific
            school media.
          </p>
          <p>
            For pupil or school media, parents and guardians should normally
            contact the school first, because the school controls whether
            particular pupil photos or videos should remain available to the
            school community. GhrianGhraffy will assist the school where needed.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How long deletion takes</h2>
          <p>
            We aim to review deletion requests as soon as reasonably possible.
            Straightforward account or profile deletion requests are normally
            handled within 30 days after we have enough information to verify
            and process the request.
          </p>
          <p>
            More complex requests, such as requests involving school-controlled
            media, active subscriptions, legal obligations, or safeguarding
            considerations, may take longer.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Contact</h2>
          <p>
            For account deletion, data deletion, or privacy questions, contact{" "}
            <a href={mailtoHref}>{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default DeleteAccount;